import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { authApi } from '@/lib/api'
import type { AuthUser, Membership, SessionResponse } from '@/lib/api-types'

const ORG_CACHE_KEY = 'agrosdr.currentOrgId'

function readCachedOrgId(): string | null {
  try {
    return localStorage.getItem(ORG_CACHE_KEY)
  } catch {
    return null
  }
}

function writeCachedOrgId(id: string | null) {
  try {
    if (id) {
      localStorage.setItem(ORG_CACHE_KEY, id)
    } else {
      localStorage.removeItem(ORG_CACHE_KEY)
    }
  } catch {
    // Private browsing and locked-down storage can throw; auth still works via cookies.
  }
}

export interface AuthContextValue {
  user: AuthUser | null
  memberships: Membership[]
  currentOrgId: string | null
  currentRole: Membership['role'] | null
  loading: boolean
  refresh: () => Promise<void>
  switchOrg: (orgId: string) => void
  signOut: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined
)

async function fetchSession(): Promise<SessionResponse> {
  const { data } = await authApi.get<SessionResponse>('/api/auth/get-session')
  return data
}

async function fetchMemberships(): Promise<Membership[]> {
  try {
    const { data } = await authApi.get<Membership[]>('/v1/users/me/memberships')
    return data ?? []
  } catch {
    return []
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [memberships, setMemberships] = useState<Membership[]>([])
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(
    readCachedOrgId
  )
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)

    try {
      const session = await fetchSession()

      if (!session.user) {
        setUser(null)
        setMemberships([])
        setCurrentOrgId(null)
        writeCachedOrgId(null)
        return
      }

      setUser(session.user)

      const nextMemberships = await fetchMemberships()
      setMemberships(nextMemberships)

      const cachedOrgId = readCachedOrgId()
      const validCached = nextMemberships.find(
        (membership) => membership.organizationId === cachedOrgId
      )
      const resolvedOrgId =
        validCached?.organizationId ??
        nextMemberships[0]?.organizationId ??
        null

      setCurrentOrgId(resolvedOrgId)
      writeCachedOrgId(resolvedOrgId)
    } catch {
      setUser(null)
      setMemberships([])
      setCurrentOrgId(null)
      writeCachedOrgId(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const refresh = useCallback(async () => {
    await load()
  }, [load])

  const switchOrg = useCallback((orgId: string) => {
    setCurrentOrgId(orgId)
    writeCachedOrgId(orgId)
  }, [])

  const signOut = useCallback(async () => {
    await authApi.post('/api/auth/sign-out')
    setUser(null)
    setMemberships([])
    setCurrentOrgId(null)
    writeCachedOrgId(null)
  }, [])

  const currentRole =
    memberships.find((membership) => membership.organizationId === currentOrgId)
      ?.role ?? null

  return (
    <AuthContext.Provider
      value={{
        user,
        memberships,
        currentOrgId,
        currentRole,
        loading,
        refresh,
        switchOrg,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
